import React, { useEffect } from 'react';
import './DashboardPage.css';
import { useAuth } from '../../context/AuthContext';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import api from '../../config/api-config';

const DashboardPage = () => {
  const { user, logout, token } = useAuth();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [boards, setBoards] = useState([]);
  const [showCreateBoard, setShowCreateBoard] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [loading, setLoading] = useState(false);
  const [boardsLoading, setBoardsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchBoards();
    }
  }, [user]);

  const fetchBoards = async () => {
    try {
      setBoardsLoading(true);
      const t = await user.getIdToken();
      const response = await api.get(`/api/user/boards?userId=${user.uid}`, {
        headers: {
          'Authorization': `Bearer ${t}`,
        }
      });
      setBoards(response.data);
    } catch (error) {
      console.error('Error fetching boards:', error);
    } finally {
      setBoardsLoading(false);
    }
  };

  const handleCreateBoard = async (e) => {
    e.preventDefault();
    if (!newBoardName.trim()) return;
    
    setLoading(true);
    try {
      const t = await user.getIdToken();
      const response = await api.post('/api/boards', 
        { name: newBoardName },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${t}`,
          }
        }
      );
      setBoards([...boards, response.data]);
      setNewBoardName('');
      setShowCreateBoard(false);
      fetchBoards(); // Refresh the boards list
    } catch (error) {
      console.error('Error creating board:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const toggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="logo">
          <img src="/logo192.png" alt="DevBoard Logo" className="logo-img" />
          <h3>DevBoard</h3>
        </div>
        <div className="profile-section">
          <img
            src={user ? user.photoURL : 'default-profile-icon.png'}
            alt="Profile"
            className="profile-picture"
          />
          <span className="logout-dots" onClick={toggleDropdown}>...</span>
          {dropdownVisible && (
            <div className="dropdown-menu">
              <button className="dropdown-button">Settings</button>
              <button className="dropdown-button" onClick={handleLogout}>Logout</button>
            </div>
          )}
        </div>
      </header>

      <section className="section-header">
        <div className="header-content">
          <h1>Welcome, {user ? user.displayName : '(name)'}!</h1>
        </div>
        <p>Game Development, Visualized. What will you create today?</p>
      </section>

      <section className="section-projects">
        <div className="section-title">
          <div className="title-with-button">
            <h2>My Boards</h2>
            {!showCreateBoard && (
              <button 
                className="add-project-button" 
                onClick={() => setShowCreateBoard(true)}
              >
                +
              </button>
            )}
          </div>
          <span><i className="fas fa-chevron-right"></i></span>
        </div>
        
        {showCreateBoard && (
          <div className="create-board-form">
            <form onSubmit={handleCreateBoard}>
              <input
                type="text"
                placeholder="Board Name"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                className="board-name-input"
              />
              <div className="form-buttons">
                <button 
                  type="button" 
                  onClick={() => setShowCreateBoard(false)}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={loading || !newBoardName.trim()}
                  className="create-button"
                >
                  {loading ? 'Creating...' : 'Create Board'}
                </button>
              </div>
            </form>
          </div>
        )}
        
        <div className="projects-grid">
          {boardsLoading ? (
            <div className="boards-loading">
              <div className="boards-loading-spinner"></div>
              <p>Loading your boards...</p>
            </div>
          ) : boards.length > 0 ? (
            boards.map((board) => (
              <Link to={`/board/${board.id}`} key={board.id} className="project-card">
                <h3>{board.name}</h3>
                <p>Owner: {board.owner === user.uid ? 'You' : board.owner}</p>
              </Link>
            ))
          ) : (
            <p className="no-boards-message">No boards yet. Create your first board!</p>
          )}
        </div>
      </section>

      <section className="section-templates">
        <div className="section-title">
          <div className="title-with-button">
            <h2>Templates</h2>
          </div>
          <span><i className="fas fa-chevron-right"></i></span>
        </div>
        <div className="templates-grid">
          <div className="template-card">
            <div className="template-icon">
              <i className="fas fa-gamepad"></i>
            </div>
            <h3>Game Design</h3>
            <p>Start with a game design template</p>
          </div>
          <div className="template-card">
            <div className="template-icon">
              <i className="fas fa-sitemap"></i>
            </div>
            <h3>Level Design</h3>
            <p>Plan your game levels</p>
          </div>
          <div className="template-card">
            <div className="template-icon">
              <i className="fas fa-users"></i>
            </div>
            <h3>Team Project</h3>
            <p>Collaborate with your team</p>
          </div>
          <div className="template-card">
            <div className="template-icon">
              <i className="fas fa-tasks"></i>
            </div>
            <h3>Task Tracker</h3>
            <p>Manage your development tasks</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
